import React from 'react'
import {
  AppState,
  DayResult,
  Plan,
  IncomeRule,
  ExpenseRule,
  BudgetItem,
  OneOffTransaction,
  BalanceAdjustment,
  BalanceTarget,
  SidebarTab,
  Transfer,
  AccountTransfer,
} from '../types'
import { formatMoney, formatDateShort, formatSigned } from '../lib/format'
import {
  summarizeRule,
  summarizeTarget,
  headlineAchievement,
  TargetAchievement,
} from '../lib/cashflowEngine'
import { ragDot, ragText, targetDot } from '../lib/ragStyles'
import { sortedByName } from '../lib/sort'
import { fromISODate } from '../lib/dateRules'
import ScopeSelect from './ScopeSelect'
import { activeInterest, describeBands, describePaymentDay, rateOn } from '../lib/interest'
import { onlyExamples } from '../lib/storage'

interface Props {
  plan: Plan
  /** The account in view, or null when showing every account together. */
  state: AppState | null
  /** An account id, or ALL_ACCOUNTS. */
  scopeValue: string
  onChangeScope: (value: string) => void
  onAddAccount: () => void
  onImportStatement: () => void
  onEditAccount: () => void
  onEditInterest: () => void
  /** Each account's figure for today, for the list shown under "All accounts". */
  todayByAccount: Map<string, DayResult | undefined>
  combinedToday?: DayResult
  activeTab: SidebarTab
  onSelectTab: (tab: SidebarTab) => void
  onAddIncome: () => void
  onEditIncome: (item: IncomeRule) => void
  onAddExpense: () => void
  onEditExpense: (item: ExpenseRule) => void
  onAddBudget: () => void
  onEditBudget: (item: BudgetItem) => void
  onAddOneOff: () => void
  onEditOneOff: (item: OneOffTransaction) => void
  onAddTransfer: () => void
  onEditTransfer: (item: Transfer) => void
  onAddAdjustment: () => void
  onEditAdjustment: (item: BalanceAdjustment) => void
  onOpenSettings: () => void
  onOpenPayoff: (item: ExpenseRule) => void
  onOpenActuals: (item: BudgetItem | ExpenseRule) => void
  onAddTarget: () => void
  onEditTarget: (item: BalanceTarget) => void
  achievements: Map<string, TargetAchievement[]>
  todayIso: string
  strictMode: boolean
  onToggleStrictMode: (v: boolean) => void
  /** Below md this is a drawer over the calendar; from md up it's a fixed column. */
  open: boolean
  onClose: () => void
}

const TABS: { id: SidebarTab; label: string }[] = [
  { id: 'income', label: 'Income' },
  { id: 'expenses', label: 'Outgoings' },
  { id: 'budgets', label: 'Budgets' },
  { id: 'oneoffs', label: 'One-offs' },
  { id: 'transfers', label: 'Transfers' },
  { id: 'adjustments', label: 'Adjustments' },
  { id: 'targets', label: 'Targets' },
]

/** `onAdd` is left out where there's nothing that could be added yet. */
function PanelHeader({ title, onAdd }: { title: string; onAdd?: () => void }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xs font-sans uppercase tracking-wide text-ink/70">{title}</h2>
      {onAdd && (
        <button onClick={onAdd} className="text-accent text-sm hover:underline leading-none">
          + Add
        </button>
      )}
    </div>
  )
}

/** When a transfer happens, in the words the other lists use. */
function summarizeTransfer(t: AccountTransfer): string {
  const when =
    t.frequency === 'once'
      ? t.date
        ? `Once, ${formatDateShort(fromISODate(t.date))}`
        : 'Once'
      : summarizeRule({ ...t, kind: 'income', dateRule: t.dateRule ?? { type: 'exact', dayOfMonth: 1 }, frequency: t.frequency })
  return `${when} · ${t.direction === 'out' ? 'to' : 'from'} ${t.counterpartyName}`
}

/** An empty list's message. A list item, since it sits inside the <ul>. */
function Empty({ children }: { children: React.ReactNode }) {
  return <li className="text-xs text-ink/70 px-2">{children}</li>
}

export default function Sidebar(props: Props) {
  const { currencySymbol } = props.plan.settings

  return (
    <aside
      className={`w-[85vw] max-w-xs md:w-72 shrink-0 border-r border-line bg-white flex flex-col
        fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out
        md:static md:h-full md:translate-x-0 md:transition-none
        ${props.open ? 'translate-x-0 shadow-xl' : '-translate-x-full'}`}
    >
      <div data-tour="starting-balance" className="p-4 border-b border-line shrink-0">
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-display text-xl text-ink leading-tight">Snapshot Money</h1>
          <button
            onClick={props.onClose}
            aria-label="Close menu"
            className="md:hidden text-ink/70 hover:text-ink text-2xl leading-none w-8 h-8 -mt-1 -mr-1 flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <ScopeSelect
          plan={props.plan}
          value={props.scopeValue}
          onChange={props.onChangeScope}
          onAddAccount={props.onAddAccount}
          className="w-full mt-3"
        />
        {props.state ? (
          <div className="mt-3 flex items-start justify-between gap-2">
            <div>
              <div className="text-[11px] font-sans uppercase tracking-wide text-ink/70">Starting balance</div>
              <div className="font-mono text-lg font-medium text-ink font-tabular leading-tight">
                {formatMoney(props.state.settings.startingBalance, currencySymbol)}
              </div>
              <div className="text-[11px] text-ink/70">
                as of {formatDateShort(fromISODate(props.state.settings.startingBalanceDate))}
              </div>
            </div>
            <button onClick={props.onEditAccount} className="text-xs text-accent hover:underline shrink-0 mt-0.5">
              Edit account
            </button>
          </div>
        ) : null}
        {props.state && <InterestSummary {...props} state={props.state} />}
        {!props.state && props.combinedToday && (
          <div className="mt-3">
            <div className="text-[11px] font-sans uppercase tracking-wide text-ink/70">Combined today</div>
            <div className={`font-mono text-lg font-medium font-tabular leading-tight ${ragText[props.combinedToday.rag]}`}>
              {formatMoney(props.combinedToday.balance, currencySymbol)}
            </div>
          </div>
        )}
        <button onClick={props.onOpenSettings} className="text-xs text-ink/70 hover:text-accent hover:underline mt-2">
          Settings
        </button>
      </div>

      {props.state ? (
        <AccountPanels {...props} state={props.state} />
      ) : (
        <div className="flex-1 overflow-y-auto p-4">
          <PanelHeader title="Accounts" onAdd={props.onAddAccount} />
          <ul className="space-y-1">
            {props.plan.accounts.map((account) => {
              const today = props.todayByAccount.get(account.id)
              return (
                <li key={account.id}>
                  <button
                    onClick={() => props.onChangeScope(account.id)}
                    className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between gap-2 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="text-ink truncate">{account.name}</div>
                      {!account.includeInTotal && <div className="text-[11px] text-ink/70">Not in the total</div>}
                    </div>
                    <span className={`font-mono font-tabular shrink-0 ${today ? ragText[today.rag] : 'text-ink/70'}`}>
                      {today ? formatMoney(today.balance, currencySymbol) : '—'}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="text-[11px] text-ink/70 mt-3 px-2">
            Pick an account to see and edit its income, outgoings and budgets.
          </p>
        </div>
      )}

      <Legend />
    </aside>
  )
}

/**
 * While an account holds nothing but the starter examples (or nothing): the
 * quick way in is a statement, which finds the pay and bills (SM-31).
 */
function StartFromStatement({ onImportStatement, onAddIncome }: Props) {
  return (
    <section aria-labelledby="start-from-statement" className="m-4 mb-0 p-3 rounded-sm border border-accent/30 bg-paper">
      <h2 id="start-from-statement" className="text-sm text-ink font-medium">
        Start from your bank statement
      </h2>
      <p className="text-xs text-ink/75 mt-1">
        Upload a month or two of statements and we'll find your pay and regular bills. They replace the examples
        below.
      </p>
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={onImportStatement}
          className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90"
        >
          Import a statement
        </button>
        <button onClick={onAddIncome} className="text-xs text-accent hover:underline">
          Add pay by hand
        </button>
      </div>
    </section>
  )
}

/** One line on what the account earns, with the way in to change it. */
function InterestSummary({ state, todayIso, onEditInterest }: Props & { state: AppState }) {
  const interest = activeInterest(state.interest)
  const current = interest ? rateOn(interest.rates, todayIso) : undefined
  return (
    <div className="mt-2 flex items-start justify-between gap-2 text-[11px]">
      {interest && current ? (
        <span className="text-ink/75">
          Interest {describeBands(current.bands, state.settings.currencySymbol)}, paid on{' '}
          {describePaymentDay(interest.paymentDay)}
        </span>
      ) : interest ? (
        <span className="text-ink/70">Interest starts at a later rate change</span>
      ) : (
        <span className="text-ink/70">No interest</span>
      )}
      <button onClick={onEditInterest} className="text-accent hover:underline shrink-0">
        {interest ? 'Edit' : 'Add'}
      </button>
    </div>
  )
}

/** The tabbed lists for one account. */
function AccountPanels(props: Props & { state: AppState }) {
  const { state, activeTab } = props
  const { currencySymbol } = state.settings

  return (
    <>
      <nav data-tour="sidebar-tabs" className="grid grid-cols-4 border-b border-line shrink-0">
        {TABS.map((tab) => {
          const active = tab.id === activeTab
          return (
            <button
              key={tab.id}
              onClick={() => props.onSelectTab(tab.id)}
              aria-current={active ? 'page' : undefined}
              className={`px-1 py-2 text-[11px] font-sans border-b-2 transition-colors ${
                active
                  ? 'border-accent text-accent bg-paper/60'
                  : 'border-transparent text-ink/70 hover:text-ink hover:bg-paper/40'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </nav>

      {/* Only the active list scrolls — the header, tabs and legend stay put. */}
      <div className="flex-1 overflow-y-auto">
        {onlyExamples(state) && <StartFromStatement {...props} />}
        {activeTab === 'income' && (
          <div data-tour="income" className="p-4">
            <PanelHeader title="Income" onAdd={props.onAddIncome} />
            <ul className="space-y-1">
              {sortedByName(state.incomes).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => props.onEditIncome(item)}
                    className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between text-sm ${!item.active ? 'opacity-40' : ''}`}
                  >
                    <div>
                      <div className="text-ink">{item.name}</div>
                      <div className="text-[11px] text-ink/70">{summarizeRule(item)}</div>
                    </div>
                    <span className="font-mono text-rag-good font-tabular">
                      {formatMoney(item.amount, currencySymbol)}
                    </span>
                  </button>
                </li>
              ))}
              {state.incomes.length === 0 && <Empty>No income sources yet.</Empty>}
            </ul>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div data-tour="outgoings" className="p-4">
            <PanelHeader title="Fixed outgoings" onAdd={props.onAddExpense} />
            <ul className="space-y-1">
              {sortedByName(state.expenses).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => props.onEditExpense(item)}
                    className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between text-sm ${!item.active ? 'opacity-40' : ''}`}
                  >
                    <div>
                      <div className="text-ink">
                        {item.name}
                        {item.isDebt && <span className="text-[10px] text-ink/70 ml-1">(debt)</span>}
                      </div>
                      <div className="text-[11px] text-ink/70">{summarizeRule(item)}</div>
                    </div>
                    <span className="font-mono text-rag-bad font-tabular">
                      {formatMoney(item.amount, currencySymbol)}
                    </span>
                  </button>
                  <button
                    onClick={() => props.onOpenActuals(item)}
                    className="text-[11px] text-accent hover:underline ml-2"
                  >
                    Planned vs actual →
                  </button>
                  {item.isDebt && (
                    <button
                      onClick={() => props.onOpenPayoff(item)}
                      className="text-[11px] text-accent hover:underline ml-2"
                    >
                      Payoff calculator →
                    </button>
                  )}
                </li>
              ))}
              {state.expenses.length === 0 && <Empty>No fixed outgoings yet.</Empty>}
            </ul>
          </div>
        )}

        {activeTab === 'budgets' && (
          <div data-tour="budgets" className="p-4">
            <PanelHeader title="Budgeted spend" onAdd={props.onAddBudget} />
            <ul className="space-y-1">
              {sortedByName(state.budgets).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => props.onEditBudget(item)}
                    className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between text-sm ${!item.active ? 'opacity-40' : ''}`}
                  >
                    <div>
                      <div className="text-ink">{item.name}</div>
                      <div className="text-[11px] text-ink/70">{summarizeRule(item)}</div>
                    </div>
                    <span className="font-mono text-rag-mediumDeep font-tabular">
                      {formatMoney(item.amount, currencySymbol)}
                    </span>
                  </button>
                  <button
                    onClick={() => props.onOpenActuals(item)}
                    className="text-[11px] text-accent hover:underline ml-2"
                  >
                    Budget vs actual →
                  </button>
                </li>
              ))}
              {state.budgets.length === 0 && <Empty>No budgets yet.</Empty>}
            </ul>
          </div>
        )}

        {activeTab === 'oneoffs' && (
          <div data-tour="oneoffs" className="p-4">
            <PanelHeader title="One-offs" onAdd={props.onAddOneOff} />
            <ul className="space-y-1">
              {sortedByName(state.oneOffs).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => props.onEditOneOff(item)}
                    className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="text-ink">{item.name}</div>
                      <div className="text-[11px] text-ink/70">{item.date}</div>
                    </div>
                    <span className={`font-mono font-tabular ${item.amount < 0 ? 'text-rag-bad' : 'text-rag-good'}`}>
                      {formatMoney(item.amount, currencySymbol)}
                    </span>
                  </button>
                </li>
                ))}
              {state.oneOffs.length === 0 && <Empty>No one-off transactions yet.</Empty>}
            </ul>
          </div>
        )}

        {activeTab === 'transfers' && (
          <div data-tour="transfers" className="p-4">
            <PanelHeader title="Transfers" onAdd={props.plan.accounts.length >= 2 ? props.onAddTransfer : undefined} />
            <ul className="space-y-1">
              {sortedByName(state.transfers ?? []).map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => props.onEditTransfer(item)}
                    className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between gap-2 text-sm ${!item.active ? 'opacity-40' : ''}`}
                  >
                    <div className="min-w-0">
                      <div className="text-ink">{item.name}</div>
                      <div className="text-[11px] text-ink/70">{summarizeTransfer(item)}</div>
                    </div>
                    <span className={`font-mono font-tabular shrink-0 ${item.direction === 'out' ? 'text-rag-bad' : 'text-rag-good'}`}>
                      {formatSigned(item.direction === 'out' ? -item.amount : item.amount, currencySymbol)}
                    </span>
                  </button>
                </li>
              ))}
              {(state.transfers ?? []).length === 0 && (
                <Empty>
                  {props.plan.accounts.length < 2
                    ? 'Transfers move money between two accounts. Add another account to set one up.'
                    : 'No transfers in or out of this account yet.'}
                </Empty>
              )}
            </ul>
          </div>
        )}

        {activeTab === 'adjustments' && (
          <div data-tour="adjustments" className="p-4">
            <PanelHeader title="Balance adjustments" onAdd={props.onAddAdjustment} />
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={props.strictMode}
                onChange={(e) => props.onToggleStrictMode(e.target.checked)}
              />
              <span className="text-xs text-ink/80">Show strict plan (ignore adjustments)</span>
            </label>
            <ul className="space-y-1">
              {[...state.adjustments]
                // Newest first — the most recent reconciliation is the one you
                // usually want to see. ISO dates compare correctly as strings.
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => props.onEditAdjustment(item)}
                      className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper text-sm ${!item.active ? 'opacity-40' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] text-ink/70">{formatDateShort(fromISODate(item.date))}</div>
                        <span className={`font-mono font-tabular ${item.delta < 0 ? 'text-rag-bad' : 'text-rag-good'}`}>
                          {formatSigned(item.delta, currencySymbol)}
                        </span>
                      </div>
                      {item.note && <div className="text-[11px] text-ink/70 truncate mt-0.5">{item.note}</div>}
                    </button>
                  </li>
                ))}
              {state.adjustments.length === 0 && <Empty>No adjustments yet.</Empty>}
            </ul>
          </div>
        )}

        {activeTab === 'targets' && (
          <div data-tour="targets" className="p-4">
            <PanelHeader title="Targets" onAdd={props.onAddTarget} />
            <ul className="space-y-1">
              {sortedByName(state.targets).map((item) => {
                const headline = headlineAchievement(props.achievements.get(item.id), props.todayIso)
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => props.onEditTarget(item)}
                      className={`w-full text-left px-2 py-1.5 rounded-sm hover:bg-paper flex items-center justify-between text-sm ${!item.active ? 'opacity-40' : ''}`}
                    >
                      <div className="min-w-0">
                        <div className="text-ink">{item.name}</div>
                        <div className="text-[11px] text-ink/70">{summarizeTarget(item, currencySymbol)}</div>
                        {headline && (
                          <div className={`text-[11px] ${headline.reached ? 'text-gold-deep' : 'text-ink/70'}`}>
                            {headline.reached ? 'Achieved' : 'On track'}{' '}
                            {formatDateShort(fromISODate(headline.achievement.date))}
                            {item.raiseBy
                              ? ` · ${formatMoney(headline.achievement.level, currencySymbol)}`
                              : ''}
                          </div>
                        )}
                        {!headline && item.active && (
                          <div className="text-[11px] text-ink/70">Not reached in the next 3 years</div>
                        )}
                      </div>
                      <span className={`w-3 h-3 rounded-sm shrink-0 ${targetDot[item.style]}`} />
                    </button>
                  </li>
                )
              })}
              {state.targets.length === 0 && <Empty>No targets yet.</Empty>}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}

function Legend() {
  return (
    <div className="p-4 border-t border-line shrink-0">
      <h2 className="text-xs font-sans uppercase tracking-wide text-ink/70 mb-2">Legend</h2>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-ink/80">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-sm ${ragDot.good}`} /> Good
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-sm ${ragDot.medium}`} /> Tight
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-sm ${ragDot.bad}`} /> Bad
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-sm ${targetDot.gold}`} /> Target
        </div>
      </div>
    </div>
  )
}
